import { DISCOVER_PAGE_KEYS } from '@/constants/state'
import type { FixedTermSortBy, FixedTermSortOrder } from '@/utils/fixedTermMarkets/sortFixedTermMarkets'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { filterNetworks } from '@vaultsfyi/common/utils'
import { useSearchState } from './useSearchState'

export type { FixedTermSortBy, FixedTermSortOrder } from '@/utils/fixedTermMarkets/sortFixedTermMarkets'

const DEFAULT_SORT_BY: FixedTermSortBy = 'tvl'
const DEFAULT_SORT_ORDER: FixedTermSortOrder = 'desc'
const SORT_COLUMNS: FixedTermSortBy[] = ['expiry', 'fixedApy', 'tvl']

export const useFixedTermMarketsSearchState = () => {
  const { searchState, updateSearchState } = useSearchState({
    debounceTimeout: SECOND_IN_MILLISECONDS / 2,
    debouncedKeys: [DISCOVER_PAGE_KEYS.fixedTerm.search],
  })
  const rawPage = Number(searchState.get(DISCOVER_PAGE_KEYS.fixedTerm.page))
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1
  const search = searchState.get(DISCOVER_PAGE_KEYS.fixedTerm.search) ?? ''
  const assets = searchState.getAll(DISCOVER_PAGE_KEYS.fixedTerm.assets)
  const protocols = searchState.getAll(DISCOVER_PAGE_KEYS.fixedTerm.protocols)
  const networks = filterNetworks(searchState.getAll(DISCOVER_PAGE_KEYS.fixedTerm.networks))
  const rawSortBy = searchState.get(DISCOVER_PAGE_KEYS.fixedTerm.sortBy)
  const sortBy = isSortBy(rawSortBy) ? rawSortBy : DEFAULT_SORT_BY
  const rawSortOrder = searchState.get(DISCOVER_PAGE_KEYS.fixedTerm.sortOrder)
  const sortOrder = isSortOrder(rawSortOrder) ? rawSortOrder : DEFAULT_SORT_ORDER

  const setPage = (page: number) => {
    updateSearchState([DISCOVER_PAGE_KEYS.fixedTerm.page, page === 1 ? undefined : page])
  }

  const setSearch = (search: string) => {
    updateSearchState([DISCOVER_PAGE_KEYS.fixedTerm.search, search])
  }

  const setAssets = (assets: string[]) => {
    updateSearchState([DISCOVER_PAGE_KEYS.fixedTerm.assets, assets], [DISCOVER_PAGE_KEYS.fixedTerm.page, undefined])
  }

  const setProtocols = (protocols: string[]) => {
    updateSearchState(
      [DISCOVER_PAGE_KEYS.fixedTerm.protocols, protocols],
      [DISCOVER_PAGE_KEYS.fixedTerm.page, undefined]
    )
  }

  const setNetworks = (networks: string[]) => {
    updateSearchState([DISCOVER_PAGE_KEYS.fixedTerm.networks, networks], [DISCOVER_PAGE_KEYS.fixedTerm.page, undefined])
  }

  const setSortBy = (nextSortBy: FixedTermSortBy) => {
    const nextSortOrder =
      sortBy === nextSortBy ? (sortOrder === 'asc' ? 'desc' : 'asc') : getDefaultSortOrder(nextSortBy)

    updateSearchState(
      [DISCOVER_PAGE_KEYS.fixedTerm.sortBy, nextSortBy === DEFAULT_SORT_BY ? undefined : nextSortBy],
      [DISCOVER_PAGE_KEYS.fixedTerm.sortOrder, nextSortOrder === DEFAULT_SORT_ORDER ? undefined : nextSortOrder],
      [DISCOVER_PAGE_KEYS.fixedTerm.page, undefined]
    )
  }

  const activeFiltersCount = [
    search.trim() !== '',
    assets.length > 0,
    protocols.length > 0,
    networks.length > 0,
  ].filter(Boolean).length

  const clearFilters = () => {
    updateSearchState(
      [DISCOVER_PAGE_KEYS.fixedTerm.search, undefined],
      [DISCOVER_PAGE_KEYS.fixedTerm.assets, undefined],
      [DISCOVER_PAGE_KEYS.fixedTerm.protocols, undefined],
      [DISCOVER_PAGE_KEYS.fixedTerm.networks, undefined],
      [DISCOVER_PAGE_KEYS.fixedTerm.page, undefined]
    )
  }

  return {
    page,
    setPage,
    search,
    setSearch,
    assets,
    setAssets,
    protocols,
    setProtocols,
    networks,
    setNetworks,
    sortBy,
    sortOrder,
    setSortBy,
    activeFiltersCount,
    clearFilters,
  } as const
}

const isSortBy = (value: string | null): value is FixedTermSortBy => {
  return SORT_COLUMNS.some((column) => column === value)
}

const isSortOrder = (value: string | null): value is FixedTermSortOrder => {
  return value === 'asc' || value === 'desc'
}

const getDefaultSortOrder = (sortBy: FixedTermSortBy): FixedTermSortOrder => {
  return sortBy === 'expiry' ? 'asc' : 'desc'
}
